/**
 * Field formatters and display labels for the Loans change-history feed.
 *
 * Used by: `LoanDetailPanel.tsx` -> `<ChangeHistory />`.
 */

const fmtCurrency = (v: number): string =>
  v.toLocaleString("en-AU", {
    style: "currency",
    currency: "AUD",
    minimumFractionDigits: 2,
  });

const fmtDate = (raw: string): string => {
  try {
    return new Date(`${raw}T00:00:00`).toLocaleDateString("en-AU", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  } catch {
    return raw;
  }
};

const DIRECTION_LABELS: Record<string, string> = {
  Loan_Granted: "Granted (money out)",
  Loan_Taken: "Taken (money in)",
};

const AGREEMENT_LABELS: Record<string, string> = {
  Formal_Contract: "Formal contract",
  Invoice_Only: "Invoice only",
  No_Documentation: "No documentation",
};

const SECURITY_LABELS: Record<string, string> = {
  Unsecured: "Unsecured",
  PPSR_Registered: "PPSR registered",
  Mortgage_Registered: "Mortgage registered",
};

const REPAYMENT_LABELS: Record<string, string> = {
  Interest_Only: "Interest only",
  Principal_And_Interest: "P&I",
  At_Call: "At call",
  Balloon: "Balloon",
};

export const LOAN_FIELD_FORMATTERS: Record<string, (value: unknown) => string> =
  {
    principalAmount: (v) =>
      typeof v === "number" ? fmtCurrency(v) : String(v ?? "\u2014"),
    outstandingBalance: (v) =>
      typeof v === "number" ? fmtCurrency(v) : String(v ?? "\u2014"),
    interestRate: (v) =>
      typeof v === "number" ? `${v}%` : String(v ?? "\u2014"),
    startDate: (v) => (typeof v === "string" && v ? fmtDate(v) : "\u2014"),
    maturityDate: (v) => (typeof v === "string" && v ? fmtDate(v) : "\u2014"),
    loanDirection: (v) => DIRECTION_LABELS[String(v)] ?? String(v ?? "\u2014"),
    agreementType: (v) => AGREEMENT_LABELS[String(v)] ?? String(v ?? "\u2014"),
    securityType: (v) => SECURITY_LABELS[String(v)] ?? String(v ?? "\u2014"),
    repaymentType: (v) => REPAYMENT_LABELS[String(v)] ?? String(v ?? "\u2014"),
    isRelatedParty: (v) => (v ? "Yes" : "No"),
    hasFormalAgreement: (v) => (v ? "Yes" : "No"),
  };

export const LOAN_FIELD_LABELS: Record<string, string> = {
  counterpartyName: "Counterparty",
  loanDirection: "Direction",
  principalAmount: "Principal amount",
  outstandingBalance: "Outstanding balance",
  interestRate: "Interest rate",
  startDate: "Start date",
  maturityDate: "Maturity date",
  agreementType: "Agreement type",
  securityType: "Security",
  repaymentType: "Repayment type",
  balanceEstimates: "Balance sources",
  notes: "Notes",
  isRelatedParty: "Related party",
  hasFormalAgreement: "Formal agreement",
};
